import React, { useState, useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Paper,
  Table,
  Button,
  TextInput,
  Group,
  Badge,
  ActionIcon,
  Modal,
  Stack,
  Alert,
  Loader,
  Pagination,
  Select,
  Box,
  Grid,
  Divider,
  Tabs
} from '@mantine/core';
import {
  IconSearch,
  IconEdit,
  IconTrash,
  IconEye,
  IconPlus,
  IconReport,
  IconTools,
  IconBrush,
  IconCreditCard,
  IconReceipt,
  IconMail
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { API_URL } from '../../../utils/constants';

function UnitsListScreenMantine() {
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUnits: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('unit_number');
  const [sortOrder, setSortOrder] = useState('asc');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUnit, setEditingUnit] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState(null);
  const [unitTypes, setUnitTypes] = useState([]);
  const [cleaningZones, setCleaningZones] = useState([]);
  const [bookingEngines, setBookingEngines] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [activeTab, setActiveTab] = useState('details');

  // Separate useEffect for initial load only
  useEffect(() => {
    console.log('UnitsListScreenMantine initial useEffect triggered');
    
    // Reset selected unit when page loads
    setSelectedUnit(null);
    
    fetchUnitTypes();
    fetchCleaningZones();
    fetchBookingEngines();
    fetchUnits();
  }, []);

  // Separate useEffect for search/sort changes
  useEffect(() => {
    if (searchTerm || sortBy || sortOrder) {
      fetchUnits();
    }
  }, [pagination.currentPage, searchTerm, sortBy, sortOrder]);

  const fetchUnitTypes = async () => {
    try {
      const response = await fetch(`${API_URL}/unit-types`);
      if (response.ok) {
        const data = await response.json();
        setUnitTypes(data);
      }
    } catch (error) {
      console.error('Error fetching unit types:', error);
    }
  };

  const fetchCleaningZones = async () => {
    try {
      const response = await fetch(`${API_URL}/cleaning-zones`);
      if (response.ok) {
        const data = await response.json();
        setCleaningZones(data);
      }
    } catch (error) {
      console.error('Error fetching cleaning zones:', error);
    }
  };

  const fetchBookingEngines = async () => {
    try {
      const response = await fetch(`${API_URL}/booking-engines`);
      if (response.ok) {
        const data = await response.json();
        setBookingEngines(data);
      }
    } catch (error) {
      console.error('Error fetching booking engines:', error);
    }
  };

  const fetchUnits = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: 20,
        sortBy,
        sortOrder
      });
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }

      const response = await fetch(`${API_URL}/units?${params}`);
      if (response.ok) {
        const data = await response.json();
        setUnits(data.units);
        setPagination(data.pagination);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Error fetching units');
      }
    } catch (error) {
      console.error('Error fetching units:', error);
      setError('Error fetching units. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUnitSelect = (unit) => {
    setSelectedUnit(unit);
    setActiveTab('details');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handleEdit = (unit) => {
    setEditingUnit({ ...unit });
    setShowEditModal(true);
  };

  const handleSaveEdit = async () => {
    try {
      const response = await fetch(`${API_URL}/units/${editingUnit.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(editingUnit),
      });

      if (response.ok) {
        const updatedUnit = await response.json();
        setUnits(prev => prev.map(unit => 
          unit.id === updatedUnit.unit.id ? updatedUnit.unit : unit
        ));
        setShowEditModal(false);
        setEditingUnit(null);
        notifications.show({
          title: 'Success',
          message: 'Unit updated successfully',
          color: 'green',
        });
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Error updating unit');
      }
    } catch (error) {
      console.error('Error updating unit:', error);
      setError('Error updating unit. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setShowEditModal(false);
    setEditingUnit(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingUnit(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleDelete = (unit) => {
    setUnitToDelete(unit);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      const response = await fetch(`${API_URL}/units/${unitToDelete.id}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        setUnits(prev => prev.filter(unit => unit.id !== unitToDelete.id));
        setShowDeleteModal(false);
        setUnitToDelete(null);
        notifications.show({
          title: 'Success',
          message: 'Unit deleted successfully',
          color: 'green',
        });
        // Refresh the list if current page becomes empty
        if (units.length === 1 && pagination.currentPage > 1) {
          setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }));
        }
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Error deleting unit');
      }
    } catch (error) {
      console.error('Error deleting unit:', error);
      setError('Error deleting unit. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  if (loading && units.length === 0) {
    return (
      <Container fluid p={0} style={{ height: '100vh' }}>
        <Group justify="center" style={{ height: '100%' }}>
          <Loader size="lg" />
        </Group>
      </Container>
    );
  }

  return (
    <Container fluid p={0} style={{ height: '100vh' }}>
      <Grid gutter={0} style={{ height: '100%' }}>
        {/* Side Menu */}
        <Grid.Col span={1.2} style={{ backgroundColor: 'var(--mantine-color-gray-0)', padding: '0.5rem', borderRight: '1px solid var(--mantine-color-gray-2)' }}>
          <Stack gap="xs">
            <Title order={6} c="dark" mb="xs" ta="center">Units</Title>
            
            {/* Primary Actions */}
            <Button 
              variant="subtle" 
              color="blue"
              fullWidth
              size="sm"
              onClick={() => setSearchTerm('')}
              leftSection={<IconSearch size="0.8rem" />}
            >
              Find
            </Button>
            <Button 
              variant="subtle" 
              color="green"
              fullWidth
              size="sm"
              leftSection={<IconPlus size="0.8rem" />}
            >
              Add
            </Button>
            <Button 
              variant="subtle" 
              color="orange"
              fullWidth
              size="sm"
              onClick={() => selectedUnit && handleEdit(selectedUnit)}
              disabled={!selectedUnit}
              leftSection={<IconEdit size="0.8rem" />}
            >
              Change
            </Button>
            <Button 
              variant="subtle" 
              color="red"
              fullWidth
              size="sm"
              onClick={() => selectedUnit && handleDelete(selectedUnit)}
              disabled={!selectedUnit}
              leftSection={<IconTrash size="0.8rem" />}
            >
              Delete
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Unit Management buttons */}
            <Button 
              variant="subtle" 
              color="cyan"
              fullWidth
              size="sm"
              leftSection={<IconReport size="0.8rem" />}
            >
              Unit Report
            </Button>
            <Button 
              variant="subtle" 
              color="teal"
              fullWidth
              size="sm"
              leftSection={<IconTools size="0.8rem" />}
            >
              Maintenance
            </Button>
            <Button 
              variant="subtle" 
              color="indigo"
              fullWidth
              size="sm"
              leftSection={<IconBrush size="0.8rem" />}
            >
              Cleaning
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Financial buttons */}
            <Button 
              variant="subtle" 
              color="violet"
              fullWidth
              size="sm"
              leftSection={<IconCreditCard size="0.8rem" />}
            >
              Post Charge
            </Button>
            <Button 
              variant="subtle" 
              color="purple"
              fullWidth
              size="sm"
              leftSection={<IconReceipt size="0.8rem" />}
            >
              Payment
            </Button>
            <Button 
              variant="subtle" 
              color="pink"
              fullWidth
              size="sm"
              leftSection={<IconReceipt size="0.8rem" />}
            >
              Refund
            </Button>
            
            <Divider color="gray.2" my="sm" />
            
            {/* Communication buttons */}
            <Button 
              variant="subtle" 
              color="grape"
              fullWidth
              size="sm"
              leftSection={<IconMail size="0.8rem" />}
            >
              Emails
            </Button>
            <Button 
              variant="subtle" 
              color="dark"
              fullWidth
              size="sm"
              leftSection={<IconReport size="0.8rem" />}
            >
              Reports
            </Button>
          </Stack>
        </Grid.Col>

        {/* Main Content */}
        <Grid.Col span={10.8} style={{ padding: '1rem' }}>
          <Stack gap="lg">
            <Group justify="space-between">
              <Title order={1}>Units List</Title>
            </Group>

            {error && (
              <Alert color="red" title="Error">
                {error}
              </Alert>
            )}

            <Paper shadow="sm" p="md" radius="md" withBorder>
              <Group mb="md">
                <TextInput
                  placeholder="Search units by number, address, or type..."
                  leftSection={<IconSearch size="1rem" />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{ flex: 1 }}
                />
                <Button onClick={handleSearch}>Search</Button>
                <Button variant="outline" onClick={() => setSearchTerm('')} disabled={!searchTerm}>
                  Clear
                </Button>
                <Button color="green" leftSection={<IconPlus size="1rem" />}>
                  Add Unit
                </Button>
              </Group>

              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th onClick={() => handleSort('unit_number')} style={{ cursor: 'pointer' }}>
                      Unit # {getSortIcon('unit_number')}
                    </Table.Th>
                    <Table.Th onClick={() => handleSort('unit_type_name')} style={{ cursor: 'pointer' }}>
                      Type {getSortIcon('unit_type_name')}
                    </Table.Th>
                    <Table.Th onClick={() => handleSort('address')} style={{ cursor: 'pointer' }}>
                      Address {getSortIcon('address')}
                    </Table.Th>
                    <Table.Th onClick={() => handleSort('cleaning_zone')} style={{ cursor: 'pointer' }}>
                      Zone {getSortIcon('cleaning_zone')}
                    </Table.Th>
                    <Table.Th onClick={() => handleSort('booking_engine')} style={{ cursor: 'pointer' }}>
                      Engine {getSortIcon('booking_engine')}
                    </Table.Th>
                    <Table.Th onClick={() => handleSort('active')} style={{ cursor: 'pointer' }}>
                      Status {getSortIcon('active')}
                    </Table.Th>
                    <Table.Th onClick={() => handleSort('clean_status')} style={{ cursor: 'pointer' }}>
                      Clean {getSortIcon('clean_status')}
                    </Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {units.map(unit => (
                    <Table.Tr 
                      key={unit.id}
                      onClick={() => handleUnitSelect(unit)}
                      style={{ 
                        cursor: 'pointer',
                        backgroundColor: selectedUnit && selectedUnit.id === unit.id ? '#ffff00' : '',
                        border: selectedUnit && selectedUnit.id === unit.id ? '2px solid #ff0000' : ''
                      }}
                    >
                      <Table.Td><strong>{unit.unit_number}</strong></Table.Td>
                      <Table.Td>{unit.unit_type_name || 'N/A'}</Table.Td>
                      <Table.Td>{unit.address || 'N/A'}</Table.Td>
                      <Table.Td>{unit.cleaning_zone || 'N/A'}</Table.Td>
                      <Table.Td>{unit.booking_engine || 'N/A'}</Table.Td>
                      <Table.Td>
                        <Badge color={unit.active ? 'green' : 'gray'}>
                          {unit.active ? 'Active' : 'Inactive'}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={unit.clean_status === 'Clean' ? 'green' : 'yellow'}>
                          {unit.clean_status}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon 
                            color="orange" 
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleEdit(unit); }}
                          >
                            <IconEdit size="1rem" />
                          </ActionIcon>
                          <ActionIcon 
                            color="red" 
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleDelete(unit); }}
                          >
                            <IconTrash size="1rem" />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>

              {pagination.totalPages > 1 && (
                <Group justify="center" mt="md">
                  <Pagination
                    value={pagination.currentPage}
                    onChange={handlePageChange}
                    total={pagination.totalPages}
                    size="sm"
                  />
                </Group>
              )}
            </Paper>

            {/* Unit Details Section */}
            {selectedUnit && (
              <Paper shadow="sm" p="md" radius="md" withBorder>
                <Tabs value={activeTab} onChange={setActiveTab}>
                  <Tabs.List>
                    <Tabs.Tab value="details">Details</Tabs.Tab>
                    <Tabs.Tab value="reservations">Reservations</Tabs.Tab>
                    <Tabs.Tab value="maintenance">Maintenance</Tabs.Tab>
                    <Tabs.Tab value="notes">Notes</Tabs.Tab>
                  </Tabs.List>

                  <Tabs.Panel value="details" pt="md">
                    <Grid>
                      <Grid.Col span={6}>
                        <Stack gap="xs">
                          <Text><strong>Unit Number:</strong> {selectedUnit.unit_number}</Text>
                          <Text><strong>Type:</strong> {selectedUnit.unit_type_name || 'N/A'}</Text>
                          <Text><strong>Address:</strong> {selectedUnit.address || 'N/A'}</Text>
                          <Text><strong>Cleaning Zone:</strong> {selectedUnit.cleaning_zone || 'N/A'}</Text>
                          <Text><strong>Booking Engine:</strong> {selectedUnit.booking_engine || 'N/A'}</Text>
                          <Text><strong>Status:</strong> 
                            <Badge color={selectedUnit.active ? 'green' : 'gray'} ml="xs">
                              {selectedUnit.active ? 'Active' : 'Inactive'}
                            </Badge>
                          </Text>
                          <Text><strong>Clean Status:</strong> 
                            <Badge color={selectedUnit.clean_status === 'Clean' ? 'green' : 'yellow'} ml="xs">
                              {selectedUnit.clean_status}
                            </Badge>
                          </Text>
                        </Stack>
                      </Grid.Col>
                      <Grid.Col span={6}>
                        <Stack gap="xs">
                          <Text><strong>Unit Type ID:</strong> {selectedUnit.unit_type_id || 'N/A'}</Text>
                          <Text><strong>Cleaning Zone ID:</strong> {selectedUnit.cleaning_zone_id || 'N/A'}</Text>
                          <Text><strong>Booking Engine ID:</strong> {selectedUnit.booking_engine_id || 'N/A'}</Text>
                        </Stack>
                      </Grid.Col>
                    </Grid>
                  </Tabs.Panel>

                  <Tabs.Panel value="reservations" pt="md">
                    <Text>Reservation History</Text>
                    <Text size="sm" c="dimmed">This tab will contain reservation history, upcoming stays, and booking information.</Text>
                  </Tabs.Panel>

                  <Tabs.Panel value="maintenance" pt="md">
                    <Text>Maintenance Records</Text>
                    <Text size="sm" c="dimmed">This tab will contain maintenance history, work orders, and repair information.</Text>
                  </Tabs.Panel>

                  <Tabs.Panel value="notes" pt="md">
                    <Text>Internal Notes</Text>
                    <Text size="sm" c="dimmed">This tab will contain internal notes, special instructions, and important information.</Text>
                  </Tabs.Panel>
                </Tabs>
              </Paper>
            )}
          </Stack>
        </Grid.Col>
      </Grid>

      {/* Edit Unit Modal */}
      <Modal opened={showEditModal} onClose={handleCancelEdit} title="Edit Unit" size="lg">
        {editingUnit && (
          <Stack gap="md">
            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Unit Number"
                  value={editingUnit.unit_number}
                  disabled
                  description="Unit number cannot be changed"
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  label="Unit Type"
                  name="unit_type_id"
                  value={editingUnit.unit_type_id || ''}
                  onChange={(value) => setEditingUnit(prev => ({ ...prev, unit_type_id: value }))}
                  data={unitTypes.filter(type => type.id && type.unit_type).map(type => ({ value: type.id.toString(), label: type.unit_type }))}
                  placeholder="Select Unit Type"
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={12}>
                <TextInput
                  label="Address"
                  name="address"
                  value={editingUnit.address || ''}
                  onChange={handleInputChange}
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <Select
                  label="Cleaning Zone"
                  name="cleaning_zone"
                  value={editingUnit.cleaning_zone || ''}
                  onChange={(value) => setEditingUnit(prev => ({ ...prev, cleaning_zone: value }))}
                  data={cleaningZones.filter(zone => zone.name).map(zone => ({ value: zone.name, label: zone.name }))}
                  placeholder="No Zone"
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  label="Booking Engine"
                  name="booking_engine"
                  value={editingUnit.booking_engine || ''}
                  onChange={(value) => setEditingUnit(prev => ({ ...prev, booking_engine: value }))}
                  data={bookingEngines.filter(engine => engine.name).map(engine => ({ value: engine.name, label: engine.name }))}
                  placeholder="No Engine"
                />
              </Grid.Col>
            </Grid>

            <Grid>
              <Grid.Col span={6}>
                <TextInput
                  label="Status"
                  name="active"
                  type="checkbox"
                  checked={editingUnit.active}
                  onChange={handleInputChange}
                />
              </Grid.Col>
              <Grid.Col span={6}>
                <Select
                  label="Clean Status"
                  name="clean_status"
                  value={editingUnit.clean_status || ''}
                  onChange={(value) => setEditingUnit(prev => ({ ...prev, clean_status: value }))}
                  data={[
                    { value: 'Clean', label: 'Clean' },
                    { value: 'Dirty', label: 'Dirty' },
                    { value: 'In Progress', label: 'In Progress' },
                    { value: 'Inspected', label: 'Inspected' }
                  ]}
                />
              </Grid.Col>
            </Grid>

            <Group justify="flex-end" mt="md">
              <Button variant="outline" onClick={handleCancelEdit}>
                Cancel
              </Button>
              <Button onClick={handleSaveEdit}>
                Save Changes
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal opened={showDeleteModal} onClose={() => setShowDeleteModal(false)} title="Confirm Delete">
        <Stack gap="md">
          <Text>
            Are you sure you want to delete unit <strong>{unitToDelete?.unit_number}</strong>?
            This action cannot be undone.
          </Text>
          <Group justify="flex-end">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button color="red" onClick={confirmDelete}>
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

export default UnitsListScreenMantine;


